import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import PageLinkBar from '@ui-parts/PageLinkBar';
import SurveyStatusDetailBox from '@ui-parts/SurveyStatusDetailBox';
import SurveyBasicInfoBox from '@ui-parts/SurveyBasicInfoBox';
import SurveyPriceBox from '@ui-parts/SurveyPriceBox';
import SurveyJudgementBox from '@ui-parts/SurveyJudgementBox';

import { calcEnqueteCollectSum } from '@functional';
import SurveyStatusModal from '../SurveyStatusModal';
import AntisocialCheckModal from '../AntisocialCheckModal';
import AntisocialCheckApprovalModal from '../AntisocialCheckApprovalModal';
import CreditCheckModal from '../CreditCheckModal';
import SurveyRejectReasonModal from '../SurveyRejectReasonModal';

import { SURVEY_STATUSES } from '~/constants';
import { ROUTES } from '~/constants/routes';
import { SURVEY_FORM_TYPE } from '~/common/constants';

const StatusArea = styled.div`
  position: relative;
`;

const HeaderBox = styled.div`
  display: flex;
  justify-content: space-between;
`;

const StatusDetailBoxArea = styled.div`
  margin-bottom: 20px;
`;

const LeftItem = styled.div`
  padding-right: 10px;
  width: 60%;
`;

const RightItem = styled.div`
  padding-left: 10px;
  width: 40%;
`;

function Header({ survey }) {
  const { t } = useTranslation();

  const judgement = useSelector((state) => state.judgement.userJudgement);
  const collectCounts = useSelector((state) => state.quotas.collectCounts);
  const surveyRejectHistory = useSelector((state) => state.surveyRejectHistory.history);
  const enqueteCollectCountsTotal = calcEnqueteCollectSum(collectCounts);

  // local states
  const [isModalOpenSurveyCheck, setIsModalOpenSurveyCheck] = useState(false);
  const [isModalOpenAntisocialCheck, setIsModalOpenAntisocialCheck] = useState(false);
  const [isModalOpenAntisocialCheckApproval, setIsModalOpenAntisocialCheckApproval] =
    useState(false);
  const [isModalOpenCreditCheck, setIsModalOpenCreditCheck] = useState(false);
  const [isOpenModalSurveyRejectHistory, setIsOpenModalSurveyRejectHistory] = useState(false);

  const isCompleted = useMemo(
    () => survey.surveyStatusId === SURVEY_STATUSES.COMPLETED,
    [survey.surveyStatusId]
  );

  const links = [
    {
      label: t('appCommon.enqueteList'),
      routePath: ROUTES.ADMIN.MYPAGE,
    },
    {
      label: `${t('myPage.surveyId')} ${survey?.id} ${survey?.name}`,
      routePath: null, // 最後の項目はリンクではありません
    },
  ];

  return (
    <>
      {survey.surveyStatusId && (
        <>
          {links !== null && <PageLinkBar links={links} />}

          <StatusArea>
            <StatusDetailBoxArea>
              <SurveyStatusDetailBox survey={survey} />
            </StatusDetailBoxArea>
          </StatusArea>
        </>
      )}

      {survey.surveyStatusId !== SURVEY_STATUSES.CANCELED && (
        <SurveyJudgementBox
          judgement={judgement}
          surveyRejectHistory={surveyRejectHistory}
          surveyStatusId={survey.surveyStatusId}
          setIsModalOpenSurveyCheck={setIsModalOpenSurveyCheck}
          setIsModalOpenAntisocialCheck={setIsModalOpenAntisocialCheck}
          setIsModalOpenCreditCheck={setIsModalOpenCreditCheck}
          setIsModalOpenAntisocialCheckApproval={setIsModalOpenAntisocialCheckApproval}
          setIsOpenModalSurveyRejectHistory={setIsOpenModalSurveyRejectHistory}
        />
      )}

      <HeaderBox>
        <LeftItem>
          <SurveyBasicInfoBox
            dataset={
              survey.type === SURVEY_FORM_TYPE.GOOGLE_FORM
                ? [
                    {
                      name: 'surveyTitle',
                      title: t('survey.SurveyBasicInfoBox.surveyTitle'),
                      label: survey.name,
                    },
                    {
                      name: 'surveyUrl',
                      title: t('survey.SurveyBasicInfoBox.surveyUrlGoogleForm'),
                      label: survey.url,
                    },
                  ]
                : [
                    {
                      name: 'surveyTitle',
                      title: t('survey.SurveyBasicInfoBox.surveyTitle'),
                      label: survey.name,
                    },
                    {
                      name: 'surveyUrl',
                      title: t('survey.SurveyBasicInfoBox.surveyUrlOriginalForm'),
                      label: survey.url.concat('1'),
                    },
                  ]
            }
            canEdit={false}
            surveyType={survey.type}
            surveyId={survey.id}
            isAdmin
          />
        </LeftItem>
        <RightItem>
          <SurveyPriceBox
            isCompleted={isCompleted}
            numberOfQuestions={survey.numberOfQuestions}
            targetSamplesTotal={survey.targetSamplesTotal}
            enqueteCollectCountsTotal={Number(enqueteCollectCountsTotal)}
            canEdit={false}
          />
        </RightItem>
      </HeaderBox>

      <AntisocialCheckModal
        open={isModalOpenAntisocialCheck}
        handleClose={() => setIsModalOpenAntisocialCheck(false)}
      />

      <AntisocialCheckApprovalModal
        open={isModalOpenAntisocialCheckApproval}
        handleClose={() => setIsModalOpenAntisocialCheckApproval(false)}
      />

      <CreditCheckModal
        open={isModalOpenCreditCheck}
        handleClose={() => setIsModalOpenCreditCheck(false)}
      />

      <SurveyStatusModal
        open={isModalOpenSurveyCheck}
        handleClose={() => setIsModalOpenSurveyCheck(false)}
      />

      {/* 却下理由履歴モーダル */}
      {surveyRejectHistory && (
        <SurveyRejectReasonModal
          open={isOpenModalSurveyRejectHistory}
          handleClose={() => {
            setIsOpenModalSurveyRejectHistory(false);
          }}
          history={surveyRejectHistory}
        />
      )}
    </>
  );
}

Header.propTypes = {
  survey: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    url: PropTypes.string,
    numberOfQuestions: PropTypes.number,
    targetSamplesTotal: PropTypes.number,
    surveyStatusId: PropTypes.number,
    type: PropTypes.number,
  }).isRequired,
};

export default Header;
